import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import KIcon from "./KIcon";
import { useCabinetLang, pluralFor, tr, currentLang } from "./i18n";
import { convertEntries, convertAmount, cabinetCurrency, canConvert } from "./money";
import SetupChatButton from "../SetupChatButton";
import { PenaltyDetails, penaltyRowsFromPayments, downloadClientDocument } from "./PaymentBits";
import { useClientProject } from "./projectScope";
import { CHANGE_TYPE_UK } from "./ChangeRequestDialog";

/*
 * Client cabinet overview — 1:1 with the approved prototype
 * (kabinet-klienta-prototype.html): project cards on top, then the selected
 * project panel (services, time timeline, tasks) + side column (attention,
 * payment, team). Real data from /client/portal.
 */

const MONTHS_UK = ["січня", "лютого", "березня", "квітня", "травня", "червня", "липня", "серпня", "вересня", "жовтня", "листопада", "грудня"];
const MON_SHORT_UK = ["січ", "лют", "бер", "кві", "тра", "чер", "лип", "сер", "вер", "жов", "лис", "гру"];
// How the segment in progress is drawn: "half" (half-filled) or "run" (a glow
// running back and forth). Two options shown to the owner, WS 23241245.
const STAGE_BAR = "half";
const STEPS = ["Заплановано", "В роботі", "Перевірка", "Погодження", "Готово"];
const STATUS = { done: ["ok", "Готово"], work: ["work", "В роботі"], wait: ["warn", "Чекає на вашу відповідь"], plan: ["plan", "Заплановано"] };
const PROJECT_STATUS = {
  proposal_sent: ["plan", "Готуємо пропозицію"],
  contract_negotiation: ["plan", "Погоджуємо договір"],
  awaiting_payment: ["warn", "Чекаємо передоплату"],
  sent_to_pool: ["work", "Запускаємо роботу"],
  in_progress: ["work", "В роботі"],
  awaiting_client: ["warn", "Чекаємо на вас"],
  completed: ["ok", "Завершено"],
};
// Timeline stage names come from the internal (English) timeline configs.

// ---------- date helpers (accept ISO and dd.mm.yyyy) ----------
const pd = (s) => {
  if (!s) return null;
  const str = String(s);
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = str.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  return null;
};
const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
const pad = (n) => String(n).padStart(2, "0");
const fd = (s) => { const x = pd(s); return x ? `${pad(x.getDate())}.${pad(x.getMonth() + 1)}.${x.getFullYear()}` : "—"; };
const fdShort = (s) => fd(s).slice(0, 5);
const fdLong = (s) => { const x = pd(s); return x ? `${x.getDate()} ${tr(MONTHS_UK[x.getMonth()])} ${x.getFullYear()}` : "—"; };
const daysTo = (s) => { const x = pd(s); return x ? Math.round((x - TODAY) / 864e5) : null; };
const dw = (n) => { const a = Math.abs(n) % 100, b = a % 10; const w = (a > 10 && a < 20) ? "днів" : b === 1 ? "день" : (b >= 2 && b <= 4) ? "дні" : "днів"; return tr(w); };
const rel = (s) => { const n = daysTo(s); if (n == null) return ""; if (n === 0) return tr("сьогодні"); if (n === 1) return tr("завтра"); if (n > 0) return `${tr("через")} ${n} ${dw(n)}`; return `${tr("прострочено на")} ${-n} ${dw(-n)}`; };
const money = (n) => Number(n || 0).toLocaleString(currentLang() === "en" ? "en-GB" : "uk-UA", { maximumFractionDigits: 2 });
const CUR_WORD = { UAH: "грн", USD: "USD", EUR: "EUR" };
const CUR_IN = { UAH: "гривнях", USD: "доларах США", EUR: "євро" };
const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };

// ---------- domain mapping ----------
// Where a task stands for the client, read off its timeline stage: the last
// stage is done, the first is not started yet. A version waiting for the
// CLIENT's approval wins — such tasks are often already done on our side.
const isDone = (t) => Boolean(t.resolved_stage?.is_last);
export const taskStatus = (t) => (t.needs_approval ? "wait" : isDone(t) ? "done" : (t.resolved_stage?.stage_index || 0) === 0 ? "plan" : "work");
export const shortName = (n) => { const parts = String(n || "").split("//").map((x) => x.trim()).filter(Boolean); return parts[parts.length - 1] || n; };

const SERVICE_KINDS = [
  // Before /spin/: "Spinhouse Animation …" is the video, not the interactive spin.
  { re: /anim|аніма/i, icon: "film", name: "Анімація", desc: "Відеоролик про проєкт для сайту, реклами та презентацій.", web: false },
  // Before the 360/tours line: "360° Video Sphere" is a video, not a tour.
  { re: /video\s*sphere|відеосфер|видеосфер/i, icon: "globe", name: "360° Відеосфера", desc: "Сферичне відео, зняте на рівні очей: «оживляє» локацію та оточення проєкту.", web: false },
  { re: /spin/i, icon: "spin", name: "Спін-хаус 360°", desc: "Інтерактивний обліт будинку: покупець обертає модель і обирає квартиру прямо на фасаді.", web: false },
  { re: /360|vr|tour|sphere|сфер|тур/i, icon: "globe", name: "360°-тури", desc: "Віртуальні тури: клієнт «заходить» у квартиру чи двір і роздивляється все навколо.", web: false },
  { re: /anim|video|відео|аніма/i, icon: "film", name: "Анімація", desc: "Відеоролик про проєкт для сайту, реклами та презентацій.", web: false },
  { re: /interior|інтер/i, icon: "image", name: "Рендери інтер’єрів", desc: "Фотореалістичні зображення квартир і місць загального користування.", web: false },
  { re: /exterior|bird|eye|facade|фасад|екстер|render|рендер/i, icon: "image", name: "Рендери екстер’єру", desc: "Фотореалістичні зображення будинку для сайту, реклами та відділу продажу.", web: false },
  { re: /floor|plan|план/i, icon: "plan", name: "Планування", desc: "2D/3D-планування квартир для сайту та відділу продажу.", web: false },
  { re: /model|модел/i, icon: "box", name: "3D-модель", desc: "Точна 3D-модель об’єкта — основа для рендерів, турів і спін-хаусу.", web: false },
  { re: /design|дизайн/i, icon: "layout", name: "Дизайн сайту", desc: "Унікальний дизайн сайту у вашому фірмовому стилі.", web: true },
  { re: /web|site|сайт|devbase|frontend|backend/i, icon: "code", name: "Вебсайт", desc: "Сайт проєкту: дизайн, верстка та адмін-панель для оновлення цін і наявності.", web: true },
];
const kindOf = (label) => SERVICE_KINDS.find((k) => k.re.test(label || "")) || null;
export const projectKind = (pr) => {
  const labels = (pr.tasks || []).map((t) => `${t.task_type || ""} ${t.name || ""}`);
  const ks = labels.map(kindOf).filter(Boolean);
  const vis = ks.some((k) => !k.web), web = ks.some((k) => k.web);
  return vis && web ? "3D-візуалізація · Вебсайт" : web ? "Вебсайт" : vis ? "3D-візуалізація" : "Проєкт";
};

const PART_ORD = ["Перший", "Другий", "Третій", "Четвертий", "П’ятий"];
const partLabel = (p) => {
  if ((p.part_total || 1) <= 1) return tr("Оплата");
  const n = tr(PART_ORD[(p.part_index || 1) - 1] || `${p.part_index}-й`);
  return `${n} ${tr("платіж")}${p.part_percent ? ` (${p.part_percent}%)` : ""}`;
};

const sumCur = (rows) => {
  const by = {};
  rows.forEach((r) => { const c = r.currency || "USD"; by[c] = (by[c] || 0) + (r.amount || 0); });
  return Object.entries(by).filter(([, v]) => v > 0);
};
// When the client picked a display currency we show one converted number with
// an ≈; otherwise the exact per-currency list, which is what the contract says.
const moneyList = (entries) => {
  const one = convertEntries(entries);
  if (one) {
    return `${one.approximate ? "≈ " : ""}${money(one.amount)} ${tr(CUR_WORD[one.currency] || one.currency)}`;
  }
  return entries.map(([c, v]) => `${money(v)} ${tr(CUR_WORD[c] || c)}`).join(" + ");
};
// Title text for a converted figure: the exact amounts behind the ≈.
const exactList = (entries) => entries.map(([c, v]) => `${money(v)} ${tr(CUR_WORD[c] || c)}`).join(" + ");

// ---------- pieces ----------
const Timeline = ({ start, deadline, done }) => {
  const { t } = useCabinetLang();
  const S = pd(start), E = pd(deadline);
  if (!S || !E || E <= S) return null;
  const total = Math.round((E - S) / 864e5);
  const passed = Math.max(0, Math.min(total, Math.round((TODAY - S) / 864e5)));
  const left = Math.round((E - TODAY) / 864e5);
  const late = left < 0 && !done;
  const pct = done ? 100 : Math.max(0, Math.min(100, ((TODAY - S) / 864e5 / total) * 100));
  const fl = pct < 14 ? "l" : pct > 86 ? "r" : "";
  const marks = [];
  for (let m = new Date(S.getFullYear(), S.getMonth() + 1, 1); m < E; m = new Date(m.getFullYear(), m.getMonth() + 1, 1)) {
    marks.push({ x: ((m - S) / (E - S)) * 100, label: MON_SHORT_UK[m.getMonth()] });
  }
  return (
    <div className={`tl ${late ? "late" : ""}`} aria-label={t("Терміни проєкту")}>
      <div className="tl-head">
        <div>
          <div className="tl-kicker"><KIcon name="clock" />{t("Терміни проєкту")}</div>
          <div className="tl-big num">
            {done ? <>{t("Здано")}<small>{fd(deadline)}</small></>
              : late ? <>{-left}<small>{dw(-left)} {t("прострочено")}</small></>
              : <>{left}<small>{dw(left)} {t("до здачі")}</small></>}
          </div>
        </div>
        <div className="tl-stats">
          <div className="tl-stat"><b className="num">{Math.round(pct)}%</b><span>{t("часу минуло")}</span></div>
          <div className="tl-stat"><b className="num">{passed} / {total}</b><span>{t("днів проєкту")}</span></div>
        </div>
      </div>
      <div className="tl-body">
        <div className="tl-track">
          {marks.map((m, i) => <span key={i} className="tl-tick" style={{ left: `${m.x}%` }} />)}
          <div className="tl-fill" style={{ width: `${pct}%` }} />
          <span className="tl-start" />
          <span className="tl-end" title={t("Здача")}><KIcon name="flag" /></span>
          {!done && (
            <span className="tl-today" style={{ left: `${pct}%` }}>
              <span className={`tl-flag ${fl}`}>{t("Сьогодні")} · {pad(TODAY.getDate())}.{pad(TODAY.getMonth() + 1)}</span>
            </span>
          )}
        </div>
        <div className="tl-months">
          {marks.filter((m) => m.x > 10 && m.x < 90 && Math.abs(m.x - pct) > 7).map((m, i) => (
            <span key={i} style={{ left: `${m.x}%` }}>{t(m.label)}</span>
          ))}
        </div>
      </div>
      <div className="tl-ends">
        <div><small>{t("Старт")}</small><b className="num">{fd(start)}</b></div>
        <div>
          <small>{t("Здача")}</small><b className="num">{fd(deadline)}</b>
          {late && <span className="note">{t("Актуальний строк уточнює ваш менеджер")}</span>}
        </div>
      </div>
    </div>
  );
};

export const TaskRow = ({ t, timeline, pmName, onOpen }) => {
  const st = taskStatus(t);
  const [cls, label] = STATUS[st];
  let meta;
  if (st === "done") meta = <span><KIcon name="cal" />{tr("Готово")} {fd(t.done_at || t.deadline)}</span>;
  else if (st === "plan") meta = <span><KIcon name="cal" />{t.deadline ? `${tr("до")} ${fd(t.deadline)}` : tr("Дату уточнюємо")}</span>;
  else meta = (<>
    <span><KIcon name="cal" />{t.deadline ? `${tr("до")} ${fd(t.deadline)} · ${rel(t.deadline)}` : tr("Строк уточнюється")}</span>
    {pmName && <span><KIcon name="user" />{pmName}</span>}
  </>);
  // A configured stage name is shown exactly as the manager typed it in
  // /settings/timelines. It used to be rewritten through a hardcoded Ukrainian
  // map, so «Project start» reached the client as «Старт» and «QA» as
  // «Тестування» — the settings page and the cabinet disagreed in writing.
  // Only our own fallback list is translated, because that text is ours.
  // backlog 29d83fe4.
  const stages = timeline?.stages?.length
    ? timeline.stages.map((s) => s.name)
    : STEPS.map((x) => tr(x));
  const cur = timeline?.stages?.length ? (timeline.current_stage_index || 0) : st === "wait" ? 3 : 1;
  // Same reading as the PM's Task Progress: stages before the current one are
  // done (green), the current one is the work in hand (half orange), the rest
  // are ahead. Round 1 painted the current stage green and the next one half
  // orange, so the cabinet sat a step ahead of the PM view (Dmytro, 05.11).
  const stepCls = (i) => (i < cur ? "d" : i === cur ? `c n${st === "wait" ? " w" : ""}` : "");
  return (
    <li className={`task ${st}`}>
      <span className="t-ic">{st === "done" && <KIcon name="check" />}</span>
      <button type="button" className="t-name" style={{ textAlign: "left" }} onClick={onOpen}>{shortName(t.name)}</button>
      <span className="t-side">
        {st === "done"
          ? <button type="button" className="lnk" onClick={onOpen}>{tr("Результат")}<KIcon name="ext" /></button>
          : <span className={`pill ${cls}`}>{tr(label)}</span>}
      </span>
      <span className="t-meta">{meta}</span>
      {(st === "work" || st === "wait") && (
        <ol className={`steps${STAGE_BAR === "run" ? " run" : ""}`} style={{ "--n": stages.length }} aria-label={`${tr("Етап задачі")}: ${stages[cur] || ""}`}>
          {stages.map((s, i) => (
            <li key={i} className={stepCls(i)}>{s}</li>
          ))}
        </ol>
      )}
    </li>
  );
};

const contactLinks = (m) => {
  const phone = (m.phone || "").replace(/[\s\-()]/g, "");
  const tg = (m.telegram || "").trim().replace(/^@/, "").replace(/^https?:\/\/t\.me\//, "");
  return [
    (tg || phone) && { label: "Telegram", icon: "send", href: `https://t.me/${tg || phone}` },
    phone && { label: "WhatsApp", icon: "chat", href: `https://wa.me/${phone.replace(/^\+/, "")}` },
    m.email && { label: "Email", icon: "mail", href: `mailto:${m.email}` },
  ].filter(Boolean);
};

const Manager = ({ m, role, about, cls, main }) => {
  const { t } = useCabinetLang();
  return (
  <article className={`mgr ${cls}`}>
    {main && <span className="mgr-main">{t("Головний контакт")}</span>}
    <div className="mgr-top">
      {/* The photo from the person's profile; initials only when there is none.
          The cabinet used to draw initials for everyone because the portal
          payload never carried avatar_url. backlog c8bb81db. */}
      <span className="mgr-ava">
        {m.avatar_url
          ? <img src={m.avatar_url} alt="" className="mgr-photo" loading="lazy"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : null}
        <span className="mgr-ini">{(m.name || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</span>
      </span>
      <div className="mgr-id"><span className="mgr-role">{t(role)}</span><span className="mgr-name">{m.name}</span></div>
    </div>
    <p className="mgr-about">{t("Пишіть щодо:")} <b>{about}</b></p>
    {m.phone && (
      <div className="mgr-phone">
        <span className="num"><KIcon name="phone" />{m.phone}</span>
        <button type="button" onClick={() => {
          try { navigator.clipboard.writeText(m.phone).then(() => toast.success(t("Номер скопійовано")), () => toast(m.phone)); } catch (_) { toast(m.phone); }
        }}>{t("Скопіювати")}</button>
      </div>
    )}
    <div className="mgr-ch">
      {contactLinks(m).map((l) => (
        <button key={l.label} type="button" onClick={() => window.open(l.href, "_blank", "noopener")}>
          <KIcon name={l.icon} />{l.label}
        </button>
      ))}
    </div>
  </article>
  );
};

// ---------- page ----------
const CabinetDashboard = ({ portal, taskTimelines = {}, viewerName, companyName, preview }) => {
  const navigate = useNavigate();
  const { t, lang } = useCabinetLang();
  const [scope, setScope] = useClientProject();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [penaltyOpen, setPenaltyOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState({});
  const [matFilter, setMatFilter] = useState("all"); // all | missing | received

  const projects = portal?.projects || [];
  const payments = portal?.payments || [];
  const approvals = portal?.tasks_awaiting_approval || [];
  const changes = portal?.task_change_requests || [];
  const requests = (portal?.action_required || []).filter((r) => r.request_type !== "payment_request");
  const briefs = (portal?.setup_tasks || []).filter((t) => t.setup_stage === "client");

  const byDeadline = (a, b) => (a.deadline || "9999").localeCompare(b.deadline || "9999");
  const active = projects.filter((p) => p.status !== "completed").sort(byDeadline);
  const archive = projects.filter((p) => p.status === "completed").sort((a, b) => (b.deadline || "").localeCompare(a.deadline || ""));
  const inP = (pid) => (x) => x.project_id === pid;
  // What a project is waiting on the client for. `urgency` orders the default
  // pick (money overdue first); `n` is how many items that project holds.
  // A request lives on ONE project, so the panel below — which is scoped to the
  // selected project — must never speak for the whole cabinet. backlog d105c8e8 / 4f9bc04c.
  const needsOf = (p) => {
    const pays = payments.filter(inP(p.id));
    const items = [...approvals, ...changes, ...requests, ...briefs].filter(inP(p.id)).length;
    const over = pays.filter((x) => x.state === "overdue").length;
    const due = pays.filter((x) => x.state === "invoiced").length;
    const n = over + due + items;
    return { n, urgency: over ? 3 : items ? 2 : due ? 1 : 0 };
  };
  // An explicit pick always wins. Without one, open on the project that needs
  // the client — opening on "first active project with tasks" is what showed
  // «Від вас нічого не потрібно» to clients who had an unanswered request on
  // another project.
  const mostNeedy = [...active].sort((a, b) => {
    const na = needsOf(a), nb = needsOf(b);
    return nb.urgency - na.urgency || nb.n - na.n || byDeadline(a, b);
  })[0];
  const current =
    projects.find((p) => p.id === scope) ||
    (mostNeedy && needsOf(mostNeedy).n ? mostNeedy : null) ||
    active.find((p) => (p.tasks || []).length) || active[0] || archive[0] || null;
  const select = (id) => setScope(id);

  const flagOf = (p) => {
    if (payments.filter(inP(p.id)).some((x) => x.state === "overdue")) return ["bad", t("Прострочена оплата")];
    if ([...approvals, ...changes, ...requests, ...briefs].some(inP(p.id))) return ["warn", t("Чекаємо на вас")];
    if (payments.filter(inP(p.id)).some((x) => x.state === "invoiced")) return ["warn", t("Рахунок до оплати")];
    return null;
  };

  const hello = viewerName ? `${t("Доброго дня")}, ${viewerName}` : companyName || t("Кабінет клієнта");
  const needsAny = payments.some((x) => x.state === "overdue" || x.state === "invoiced") || approvals.length || changes.length || requests.length || briefs.length;

  if (!current) {
    return (
      <main className="main" id="top">
        <header className="top"><div><p className="eyebrow">{t("Кабінет клієнта")}{companyName ? ` · ${companyName}` : ""}</p><h1 className="hello">{hello}</h1></div></header>
        <section className="card"><p style={{ color: "var(--muted)" }}>{t("Проєктів поки немає. Щойно ми почнемо роботу, він з’явиться тут.")}</p></section>
      </main>
    );
  }

  // ----- selected project data -----
  const tasks = current.tasks || [];
  const dn = tasks.filter(isDone).length;
  const pPays = payments.filter(inP(current.id));
  const pm = current.project_manager;
  const sales = current.sales_manager && current.sales_manager.name !== pm?.name ? current.sales_manager : null;
  const [stCls, stLabel] = PROJECT_STATUS[current.status] || ["work", "В роботі"];
  const contract = current.contract || {};

  // Services = the project's stated scope (backend `scope_services`: planned,
  // else what the payments imply), each with the tasks that deliver it. Before,
  // services were guessed from task names, which showed one service on a
  // four-service project and a fee payment as "Other". WS 23241245.
  const svcStatus = (ts) => {
    if (!ts.length) return current.status === "completed" ? "done" : "plan";
    const sts = ts.map(taskStatus);
    return sts.every((s) => s === "done") ? "done" : sts.some((s) => s === "wait") ? "wait" : sts.some((s) => s !== "plan") ? "work" : "plan";
  };
  const allMaterials = current.service_materials || [];
  // Folded match: type names carry stray spaces ("360° Video Sphere "), and an
  // exact comparison lost that service's whole list on the way to the client.
  const fold = (x) => String(x || "").toLowerCase().replace(/[^a-z0-9а-яіїєґ]+/g, "");
  const materialsOf = (key) => allMaterials.filter((m) => (m.service_key || fold(m.service)) === key);
  const scopeList = current.scope_services || [];
  let services;
  if (scopeList.length) {
    services = scopeList.map((sv) => {
      const ts = tasks.filter((x) => (sv.task_ids || []).includes(x.id));
      return { kind: kindOf(sv.name), name: sv.name, tasks: ts, status: svcStatus(ts), materials: materialsOf(sv.materials_key || fold(sv.name)), pending: Boolean(sv.materials_pending) };
    });
  } else {
    // No scope and no payments yet: fall back to grouping tasks by kind.
    const svcMap = new Map();
    tasks.forEach((x) => {
      const k = kindOf(`${x.task_type || ""} ${x.name || ""}`);
      const key = k ? k.name : (x.task_type || tr("Інше"));
      if (!svcMap.has(key)) svcMap.set(key, { kind: k, name: key, tasks: [] });
      svcMap.get(key).tasks.push(x);
    });
    services = [...svcMap.values()].map((g) => ({ ...g, status: svcStatus(g.tasks), materials: materialsOf(fold(g.name)) }));
  }
  // Materials of services no longer in scope are not asked for any more.
  const materials = services.flatMap((v) => v.materials);
  const materialsGot = materials.filter((m) => m.received).length;
  const materialsMissing = materials.length - materialsGot;
  const servicesByMaterial = {};
  materials.forEach((m) => {
    const k = String(m.name || "").toLowerCase().replace(/[^a-z0-9а-яіїєґ]+/g, "");
    (servicesByMaterial[k] = servicesByMaterial[k] || new Set()).add(m.service);
  });
  const twinsOf = (m) => [...(servicesByMaterial[String(m.name || "").toLowerCase().replace(/[^a-z0-9а-яіїєґ]+/g, "")] || [])].filter((x) => x !== m.service);
  const openService = (name) => {
    setSvcOpen((o) => ({ ...o, [name]: true }));
    setTimeout(() => document.getElementById(`svc-${name}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // attention items
  const overdue = pPays.filter((x) => x.state === "overdue").sort((a, b) => (a.invoice_due_date || "").localeCompare(b.invoice_due_date || ""));
  const dueSoon = pPays.filter((x) => x.state === "invoiced").sort((a, b) => (a.invoice_due_date || "").localeCompare(b.invoice_due_date || ""));
  const pApprovals = approvals.filter(inP(current.id));
  const pChanges = changes.filter(inP(current.id));
  const pRequests = requests.filter(inP(current.id));
  const pBriefs = briefs.filter(inP(current.id));
  const penaltyRule = current.penalty_rule?.enabled ? current.penalty_rule : null;
  const penRows = penaltyRowsFromPayments(pPays, { [current.id]: current.penalty_rule });
  const accrued = penRows.reduce((a, r) => a + (r.penalty?.amount > 0 ? r.penalty.amount : 0), 0);
  const perDay = penRows.reduce((a, r) => a + (r.penalty?.amount > 0 ? ((r.penalty.base_amount || 0) * (r.penalty.rate_per_day || 0)) / 100 : 0), 0);
  const actions = [
    ...pBriefs.map((t) => ({ key: `b${t.id}`, title: tr("Заповніть бриф проєкту"), text: `${t.name || tr("Бриф")} — ${tr("без нього ми не можемо стартувати.")}`, brief: t })),
    ...pApprovals.map((a) => ({
      key: `a${a.task_id}`,
      title: `${t("Потрібна ваша відповідь до")} ${fd(a.deadline)}`,
      text: `${t("Погодьте")} «${shortName(a.task_name)}»${a.version_number ? ` (${t("версія")} ${a.version_number})` : ""}. ${t("Після цього продовжимо роботу.")}`,
      btn: t("Переглянути"), go: () => navigate(`/client/tasks/${a.task_id}`),
    })),
    ...pChanges.map((c) => ({
      key: `c${c.id}`, title: t(CHANGE_TYPE_UK[c.change_type] || "Зміна в задачі"),
      text: `${t("Погодьте зміну для")} «${shortName(c.task_name)}».`, btn: t("Розглянути"), go: () => navigate("/client/requests?seg=changes"),
    })),
    ...pRequests.map((r) => ({
      key: `r${r.id}`, title: `${t("Потрібна ваша відповідь до")} ${fd(r.deadline)}`,
      text: r.title, btn: t("Відповісти"), go: () => navigate("/client/requests?seg=info"),
    })),
  ];
  const shownActions = actions.slice(0, 2);
  // Projects other than the open one that are waiting on the client.
  const elsewhere = active.filter((p) => p.id !== current.id && needsOf(p).n > 0)
    .sort((a, b) => needsOf(b).urgency - needsOf(a).urgency);
  const elsewhereCount = elsewhere.reduce((a, p) => a + needsOf(p).n, 0);
  const nCount = (overdue.length ? 1 : 0) + (dueSoon.length ? 1 : 0) + actions.length + (materialsMissing > 0 && current.status !== "completed" ? 1 : 0);

  // payment card
  const curs = [...new Set(pPays.map((x) => x.currency || "USD"))];
  const single = curs.length === 1;
  const cur = curs[0];
  const sum = (rows) => rows.reduce((a, r) => a + (r.amount || 0), 0);
  const tTotal = sum(pPays), tPaid = sum(pPays.filter((x) => x.state === "paid")), tOver = sum(overdue), tDue = sum(dueSoon);
  const tRest = tTotal - tPaid - tOver - tDue;
  const nextPlanned = pPays.filter((x) => x.state === "not_invoiced").sort((a, b) => (a.month || "").localeCompare(b.month || ""))[0];
  const restNote = nextPlanned
    ? nextPlanned.part_index === nextPlanned.part_total && nextPlanned.part_total > 1 ? t("Після здачі проєкту") : t("Рахунок надішлемо, коли настане час")
    : "";
  const allPaid = pPays.length > 0 && pPays.every((x) => x.state === "paid");
  const openInvoice = (p) => (p.doc_ready === false ? toast.info(t("Рахунок ще готує менеджер")) : downloadClientDocument(p.id, "invoice"));

  return (
    <main className="main" id="top">
      <header className="top">
        <div>
          <p className="eyebrow">{t("Кабінет клієнта")}{companyName ? ` · ${companyName}` : ""}</p>
          <h1 className="hello">{hello}</h1>
        </div>
        <button type="button" className="icon-btn" aria-label={t("Сповіщення")} onClick={() => navigate("/client/requests")}>
          <KIcon name="bell" />{needsAny ? <span className="dot" /> : null}
        </button>
      </header>

      <section className="switch" aria-label={t("Ваші проєкти")}>
        <div className="sh">
          <h2>{t("Ваші проєкти")}</h2>
          <span className="cnt">
            {active.length} {pluralFor(lang, active.length, ["активний", "активні", "активних"])}
            {archive.length ? ` · ${archive.length} ${pluralFor(lang, archive.length, ["завершений", "завершені", "завершених"])}` : ""}
          </span>
          <span className="right">{t("Оберіть проєкт, щоб побачити його задачі та оплати")}</span>
        </div>
        <div className="proj-row" role="tablist">
          {[...active, ...(archive.some((a) => a.id === current.id) ? [current] : [])].map((p) => {
            // `nt` not `t`: `t` is the translator in this scope.
            const nt = (p.tasks || []).length, d = (p.tasks || []).filter(isDone).length;
            const flag = flagOf(p);
            return (
              <button key={p.id} type="button" className="pc" role="tab" aria-selected={p.id === current.id} onClick={() => select(p.id)}>
                <div className="pc-top">
                  <span>{t(projectKind(p))}</span>
                  {flag && <span className={`pc-flag ${flag[0]}`}>{flag[1]}</span>}
                  <span className="pc-sel"><KIcon name="check" />{t("Відкрито")}</span>
                </div>
                <div className="pc-name">{p.name}</div>
                <div className="bar"><span className="fill" style={{ width: `${nt ? Math.round((d / nt) * 100) : 0}%` }} /></div>
                <div className="pc-foot">
                  <span className="num">{nt ? `${d} ${t("з")} ${nt} ${t("задач готово")}` : t("Задачі ще не створені")}</span>
                  <span className="num">{p.deadline ? `${t("Здача")} ${fdShort(p.deadline)}` : ""}</span>
                </div>
              </button>
            );
          })}
          {archive.length > 0 && (
            <button type="button" className="pc pc-archive" aria-expanded={archiveOpen} onClick={() => setArchiveOpen((v) => !v)}>
              <div className="pc-top"><KIcon name="archive" /><span>{t("Архів")}</span></div>
              <div><b>{archive.length} {pluralFor(lang, archive.length, ["завершений проєкт", "завершені проєкти", "завершених проєктів"])}</b></div>
              <div className="pc-foot"><span>{archiveOpen ? t("Сховати") : t("Показати")}</span></div>
            </button>
          )}
        </div>
        {archiveOpen && (
          <ul className="archive-list">
            {archive.map((a) => (
              <li key={a.id} style={{ cursor: "pointer" }} onClick={() => select(a.id)}>
                <b>{a.name}</b><span>{t(projectKind(a))}{a.deadline ? ` · ${t("здано")} ${fd(a.deadline)}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="cols">
        <section className="card panel" aria-label={`${t("Проєкт")} ${current.name}`}>
          <div className="meta-row">
            <div className="ptitle"><h2>{current.name}</h2><span className={`pill ${stCls}`}>{t(stLabel)}</span></div>
            <div className="links">
              {current.communication_group && (
                <button type="button" className="btn sm" onClick={() => window.open(current.communication_group, "_blank", "noopener")}><KIcon name="chat" />{t("Чат проєкту")}</button>
              )}
              {current.materials_link && (
                <button type="button" className="btn sm" onClick={() => window.open(current.materials_link, "_blank", "noopener")}><KIcon name="folder" />{t("Матеріали")}</button>
              )}
              {(contract.number || contract.link) && (
                <button type="button" className="btn sm" title={contract.date ? `${t("Договір від")} ${fd(contract.date)}` : t("Договір")}
                  onClick={() => (contract.link ? window.open(contract.link, "_blank", "noopener") : toast.info(contract.date ? `${t("Договір від")} ${fd(contract.date)}` : t("Договір")))}>
                  <KIcon name="file" />{t("Договір")}{contract.number && <span className="mono" style={{ color: "var(--faint)" }}>№ {contract.number}</span>}
                </button>
              )}
            </div>
          </div>

          {services.length > 0 && (
            <div className="svc" id="materials">
              <div className="sh"><KIcon name="box" /><h3>{t("Що ми робимо для вас")}</h3>
                <span className="cnt">{services.length} {pluralFor(lang, services.length, ["послуга", "послуги", "послуг"])} {t("в проєкті")}</span></div>
              {materials.length > 0 && (
                <>
                  <p className="svc-note">{t("Відкрийте послугу, щоб побачити, які матеріали для неї потрібні від вас. Позначені «Не надано» надсилайте в чат проєкту або менеджеру.")}</p>
                  <div className="mat-sum">
                    <span className="mat-count num"><b>{materialsGot}/{materials.length}</b>
                      <span className="bar"><span className="fill ok" style={{ width: `${Math.round((materialsGot / materials.length) * 100)}%` }} /></span>
                      {t("надано")}</span>
                    <span className="seg" role="tablist">
                      {[["all", "Усі", materials.length], ["missing", "Потрібно надати", materialsMissing], ["received", "Надано", materialsGot]].map(([k, label, n]) => (
                        <button key={k} type="button" role="tab" aria-selected={matFilter === k} onClick={() => setMatFilter(k)}>{t(label)}<span>{n}</span></button>
                      ))}
                    </span>
                  </div>
                  <div className="mat-tools">
                    <button type="button" className="lnk" onClick={() => setSvcOpen(Object.fromEntries(services.map((v) => [v.name, true])))}>{t("Розгорнути все")}</button>
                    <button type="button" className="lnk" onClick={() => setSvcOpen({})}>{t("Згорнути все")}</button>
                  </div>
                </>
              )}
              <div className="sv-list">
                {services.map((v) => {
                  const n = v.materials.length, g = v.materials.filter((m) => m.received).length;
                  const shown = v.materials.filter((m) => matFilter === "all" || (matFilter === "missing" ? !m.received : m.received));
                  const isOpen = Boolean(svcOpen[v.name]);
                  return (
                    <article key={v.name} id={`svc-${v.name}`} className={`sv-row ${v.status} ${isOpen ? "open" : ""}`}>
                      <button type="button" className="sv-head" aria-expanded={isOpen} onClick={() => setSvcOpen((o) => ({ ...o, [v.name]: !isOpen }))}>
                        <span className="sv-ic"><KIcon name={v.kind?.icon || "box"} /></span>
                        <span className="sv-title"><b>{v.name}</b>{v.kind?.desc && <small>{t(v.kind.desc)}</small>}</span>
                        <span className={`pill ${STATUS[v.status][0]}`}>{t(STATUS[v.status][1])}</span>
                        {n > 0 ? (
                          <span className="sv-mat">
                            <span className="num"><b>{g}</b> / {n}</span>
                            <span className="bar"><span className="fill ok" style={{ width: `${Math.round((g / n) * 100)}%` }} /></span>
                            <small className={g === n ? "ok" : "info"}>{g === n ? `✓ ${t("Усе отримали")}` : `${t("Потрібно ще")} ${n - g}`}</small>
                          </span>
                        ) : <span className="sv-mat" />}
                        <span className="sv-chev"><KIcon name="chevron" /></span>
                      </button>
                      {isOpen && (
                        <div className="sv-body">
                          {n > 0 ? (
                            <>
                              {shown.map((m) => {
                                const twins = twinsOf(m);
                                return (
                                  <div key={m.id} className={`mi ${m.received ? "ok" : "wait"}`}>
                                    <span className="mi-ic">{m.received ? <KIcon name="check" /> : "!"}</span>
                                    <div className="mi-txt">
                                      <div className="mi-name">{m.name}
                                        {twins.length > 0 && <span className="mi-twin" title={t("Цей самий матеріал потрібен і для інших послуг")}>⇄ {twins.join(", ")}</span>}
                                        {m.manual && <span className="mi-man">{t("додано менеджером")}</span>}
                                      </div>
                                      {m.comment && <div className="mi-note">{t("Менеджер")}: {m.comment}</div>}
                                    </div>
                                    <span className={`mi-st ${m.received ? "ok" : "info"}`}
                                      title={m.received && m.received_at ? `${t("Отримано")} · ${fd(m.received_at)}` : undefined}>
                                      {m.received ? t("Надано") : t("Не надано")}
                                    </span>
                                  </div>
                                );
                              })}
                              {shown.length === 0 && <p className="mi-empty">{matFilter === "missing" ? t("Усе з цього списку ви вже надали.") : t("Поки нічого не отримали.")}</p>}
                            </>
                          ) : v.pending ? (
                            // No list on the project yet — not "nothing needed": the
                            // client used to read the task name ("Website") here
                            // instead of a materials list.
                            <p className="mi-empty">{t("Перелік матеріалів для цієї послуги готує ваш менеджер — щойно він буде готовий, ви побачите його тут.")}</p>
                          ) : <p className="mi-empty">{t("Від вас для цієї послуги нічого не потрібно.")}</p>}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
              {materialsMissing > 0 && (
                <div className="mat-foot">
                  <span>{t("Не знаєте, що надіслати? Напишіть менеджеру, підкажемо.")}</span>
                  {current.communication_group && (
                    <button type="button" className="btn sm" onClick={() => window.open(current.communication_group, "_blank", "noopener")}><KIcon name="chat" />{t("Надіслати файли в чат")}</button>
                  )}
                </div>
              )}
            </div>
          )}

          <Timeline start={current.start_date} deadline={current.deadline} done={current.status === "completed"} />

          <div className="tasks" id="tasks">
            <div className="sh"><KIcon name="tasks" /><h3>{t("Задачі проєкту")}</h3><span className="cnt num">{dn} {t("з")} {tasks.length} {t("готово")}</span></div>
            {tasks.length ? (
              <ol className="tlist">
                {[...tasks]
                  .sort((a, b) => ({ done: 0, wait: 1, work: 2, plan: 3 }[taskStatus(a)] - { done: 0, wait: 1, work: 2, plan: 3 }[taskStatus(b)]) || (a.deadline || "").localeCompare(b.deadline || ""))
                  .map((t) => (
                    <TaskRow key={t.id} t={t} timeline={taskTimelines[t.id]} pmName={pm?.name}
                      onOpen={() => navigate(`/client/tasks/${t.id}`)} />
                  ))}
              </ol>
            ) : <p style={{ color: "var(--muted)", fontSize: 13 }}>{t("Задачі з’являться тут, щойно почнеться робота.")}</p>}
          </div>
        </section>

        <div className="stack">
          <section className="card notif" aria-label={t("Сповіщення")}>
            <div className="sh"><KIcon name="bell" /><h3>{t("Потрібна ваша увага")}</h3>{nCount > 0 && <span className="badge">{nCount}</span>}</div>
            {overdue.length > 0 && (() => {
              const late = -daysTo(overdue[0].invoice_due_date);
              const first = overdue[0];
              const what = overdue.length === 1
                ? `${partLabel(first)} — ${first.base_name || first.name}`
                : `${overdue.length} ${pluralFor(lang, overdue.length, ["рахунок", "рахунки", "рахунків"])}: ${overdue.map((x) => x.base_name || x.name).slice(0, 2).join(", ")}`;
              return (
                <div className="ntf bad" role="alert">
                  <div className="ntf-h"><KIcon name="alert" />{t("Оплата прострочена на")} {late} {dw(late)}</div>
                  <div className="ntf-amt num">{moneyList(sumCur(overdue))}</div>
                  <p>{what}. {t("Термін оплати був")} {fdLong(first.invoice_due_date)}.</p>
                  {penaltyRule && (accrued > 0 ? (
                    <p>{t("Нараховано пеню")} <b style={{ color: "var(--bad)" }}>${money(accrued)}</b>{perDay > 0 ? ` · +$${Math.round(perDay)} ${t("щодня")}` : ""}{" "}
                      <button type="button" className="lnk" onClick={() => setPenaltyOpen((v) => !v)}>{penaltyOpen ? t("Згорнути") : t("Як рахується")}</button></p>
                  ) : (
                    <p>{t("Пеня почне нараховуватись через")} {Math.max(1, (penaltyRule.grace_period_days || 0) - late)} {dw(Math.max(1, (penaltyRule.grace_period_days || 0) - late))}{" "}
                      <button type="button" className="lnk" onClick={() => setPenaltyOpen((v) => !v)}>{penaltyOpen ? t("Згорнути") : t("Як рахується")}</button></p>
                  ))}
                  {penaltyOpen && penaltyRule && <PenaltyDetails rows={penRows} rule={penaltyRule} />}
                  <div className="links">
                    <button type="button" className="btn danger sm" onClick={() => openInvoice(first)}><KIcon name="file" />{t("Відкрити рахунок")}</button>
                    <button type="button" className="btn sm" onClick={() => navigate(`/client/payments?project=${current.id}`)}>{t("Реквізити")}</button>
                  </div>
                </div>
              );
            })()}
            {dueSoon.length > 0 && (
              <div className="ntf warn">
                <div className="ntf-h"><KIcon name="wallet" />{t("Рахунок до оплати до")} {fd(dueSoon[0].invoice_due_date)}</div>
                <div className="ntf-amt num">{moneyList(sumCur(dueSoon))}</div>
                <p>{partLabel(dueSoon[0])} — {dueSoon[0].base_name || dueSoon[0].name}{dueSoon[0].invoice_due_date ? ` · ${rel(dueSoon[0].invoice_due_date)}` : ""}.</p>
                <div className="links">
                  <button type="button" className="btn primary sm" onClick={() => openInvoice(dueSoon[0])}><KIcon name="file" />{t("Відкрити рахунок")}</button>
                  <button type="button" className="btn sm" onClick={() => navigate(`/client/payments?project=${current.id}`)}>{t("Реквізити")}</button>
                </div>
              </div>
            )}
            {shownActions.map((a) => (
              <div key={a.key} className="ntf warn">
                <div className="ntf-h"><KIcon name="clock" />{t(a.title)}</div>
                <p>{t(a.text)}</p>
                <div className="links">
                  {a.brief
                    ? <SetupChatButton taskId={a.brief.id} serviceName={a.brief.name} serviceType={a.brief.service_type} canEdit variant="button" />
                    : <button type="button" className="btn primary sm" onClick={a.go}>{t(a.btn)}</button>}
                </div>
              </div>
            ))}
            {materialsMissing > 0 && current.status !== "completed" && (
              <div className="ntf info">
                <div className="ntf-h"><KIcon name="folder" />{t("Надішліть матеріали для роботи")}</div>
                <p>{t("Щоб ми не зупиняли роботу, надайте ще")} {materialsMissing} {pluralFor(lang, materialsMissing, ["матеріал", "матеріали", "матеріалів"])}:</p>
                <div className="mat-need">
                  {services.filter((v) => v.materials.some((m) => !m.received)).map((v) => {
                    const miss = v.materials.filter((m) => !m.received);
                    return (
                      <div key={v.name} className="mat-need-svc">
                        <div className="mat-need-h"><b>{v.name}</b><span>{t("не надано")} {miss.length}</span></div>
                        <ul>{miss.slice(0, 3).map((m) => <li key={m.id}>{m.name}</li>)}</ul>
                        {miss.length > 3 && <button type="button" className="lnk" onClick={() => { setMatFilter("missing"); openService(v.name); }}>{t("і ще")} {miss.length - 3} →</button>}
                      </div>
                    );
                  })}
                </div>
                <div className="links">
                  <button type="button" className="btn primary sm" onClick={() => {
                    // Opens what is missing but keeps the filter on "all": it used to
                    // switch to "missing" for the whole list, and from then on the
                    // client saw only what they had NOT sent — received items vanished.
                    const need = services.filter((v) => v.materials.some((m) => !m.received));
                    setSvcOpen((o) => ({ ...o, ...Object.fromEntries(need.map((v) => [v.name, true])) }));
                    if (need[0]) openService(need[0].name);
                  }}>{t("Показати, що потрібно")}</button>
                  {current.communication_group && (
                    <button type="button" className="btn sm" onClick={() => window.open(current.communication_group, "_blank", "noopener")}>{t("Надіслати в чат")}</button>
                  )}
                </div>
              </div>
            )}
            {actions.length > shownActions.length && (
              <button type="button" className="lnk" onClick={() => navigate("/client/requests")}>
                {t("І ще")} {actions.length - shownActions.length} — {t("усі запити")}<KIcon name="ext" />
              </button>
            )}
            {nCount === 0 && (elsewhere.length > 0 ? (
              // Clean here, but not everywhere — saying "nothing is needed"
              // would be false, and the client has no way to see it is false.
              <div className="ntf warn">
                <div className="ntf-h"><KIcon name="clock" />{t("На цьому проєкті від вас нічого не потрібно")}</div>
                <p>{elsewhereCount} {pluralFor(lang, elsewhereCount, ["запит чекає", "запити чекають", "запитів чекають"])}{" "}
                  {t("на інших проєктах")}: {elsewhere.slice(0, 2).map((p) => p.name).join(", ")}{elsewhere.length > 2 ? "…" : ""}</p>
                <div className="links">
                  <button type="button" className="btn primary sm" onClick={() => select(elsewhere[0].id)}>
                    {t("Перейти")} — {elsewhere[0].name}
                  </button>
                  <button type="button" className="btn sm" onClick={() => navigate("/client/requests")}>{t("усі запити")}</button>
                </div>
              </div>
            ) : (
              <div className="ntf-ok"><span className="ck"><KIcon name="check" /></span>
                <div><b>{t("Від вас нічого не потрібно")}</b>{t("Ми напишемо, коли знадобиться ваша відповідь або оплата.")}</div></div>
            ))}
          </section>

          {pPays.length > 0 && (
            <section className="card pay" id="pay" aria-label={t("Оплата")}>
              <div className="sh"><KIcon name="wallet" /><h3>{t("Оплата")}</h3>
                <span className="right">{canConvert() ? `${t("Приблизно в")} ${t(CUR_WORD[cabinetCurrency()] || cabinetCurrency())} ${t("за курсом НБУ")}` : single ? `${t("Суми в")} ${t(CUR_IN[cur] || cur)}, ${t("як у договорі")}` : t("Суми у валютах договору")}</span></div>
              <div className="total">
                <span className="total-l">{t("Вартість проєкту")}</span>
                <span className="total-v num">
                  {single && !canConvert() ? <>{money(tTotal)} <small>{t(CUR_WORD[cur] || cur)}</small></> : moneyList(sumCur(pPays))}
                </span>
                {single && tTotal > 0 && (
                  <div className="bar">
                    <span className="ok" style={{ width: `${(tPaid / tTotal) * 100}%` }} />
                    {tOver > 0 && <span className="bad" style={{ width: `${(tOver / tTotal) * 100}%` }} />}
                    {tDue > 0 && <span style={{ width: `${(tDue / tTotal) * 100}%`, background: "var(--warn)" }} />}
                    {tRest > 0 && <span className="rest" style={{ width: `${(tRest / tTotal) * 100}%` }} />}
                  </div>
                )}
                <ul className="brk">
                  <li><span className="k"><i style={{ background: "var(--ok)" }} />{t("Сплачено")}</span>
                    <span className="v ok num">{single && !canConvert() ? `${money(tPaid)} ${t(CUR_WORD[cur] || cur)}` : moneyList(sumCur(pPays.filter((x) => x.state === "paid"))) || "0"}</span></li>
                  {overdue.length > 0 && (
                    <li><span className="k"><i style={{ background: "var(--bad)" }} /><span>{t("Прострочено")}<small>{t("термін був")} {fd(overdue[0].invoice_due_date)}</small></span></span>
                      <span className="v bad num">{moneyList(sumCur(overdue))}</span></li>
                  )}
                  {dueSoon.length > 0 && (
                    <li><span className="k"><i style={{ background: "var(--warn)" }} /><span>{t("До оплати")}<small>до {fd(dueSoon[0].invoice_due_date)}</small></span></span>
                      <span className="v num" style={{ color: "var(--warn)" }}>{moneyList(sumCur(dueSoon))}</span></li>
                  )}
                  {pPays.some((x) => x.state === "not_invoiced") && (
                    <li><span className="k"><i style={{ background: "var(--surface-3)" }} /><span>{t("Залишок")}<small>{restNote}</small></span></span>
                      <span className="v num">{moneyList(sumCur(pPays.filter((x) => x.state === "not_invoiced")))}</span></li>
                  )}
                </ul>
                <button type="button" className="lnk" style={{ alignSelf: "flex-start" }} onClick={() => navigate(`/client/payments?project=${current.id}`)}>
                  {t("Усі платежі, рахунки й акти")}<KIcon name="ext" />
                </button>
              </div>
              {allPaid && (
                <div className="paid-all"><span className="ck"><KIcon name="check" /></span>
                  <div><b>{t("Проєкт сплачено повністю")}</b><span>{t("Більше нічого платити не потрібно")}</span></div></div>
              )}
            </section>
          )}

          {(pm || sales) && (
            <section className="team" aria-label={t("Ваша команда")}>
              <div className="sh"><KIcon name="team" /><h3>{t("Ваша команда")}</h3><span className="right">{t("На зв'язку пн–пт, 9:00–19:00")}</span></div>
              {pm && <Manager m={pm} role={t("Менеджер проєкту")} about={t("хід робіт, правки, терміни")} cls="pm" main />}
              {sales && <Manager m={sales} role={t("Менеджер з продажу")} about={t("договір, рахунки, нові задачі")} cls="sales" />}
            </section>
          )}
        </div>
      </div>
      {preview && null}
    </main>
  );
};

export default CabinetDashboard;
